import React from 'react';
import { Box, Chip, SxProps, Theme, Typography } from '@mui/material';
import { UserInfo } from '../../../types/chatMessageDeletionHistory';
import { Link } from 'react-router-dom';
import { NURSE_TYPE_THEME } from '../../../types/nurse';
import { PATIENT_THEME } from '../../../types/patient';

interface UserRoleChipProps {
  userInfo: UserInfo | null;
  userType: string;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  position?: '親' | '子';
}

/**
 * ユーザーロールをチップ形式で表示するコンポーネント
 * 対話一覧の看護師/患者表示と同じデザイン
 */
const UserRoleChip: React.FC<UserRoleChipProps> = ({ userInfo, userType, size = 'medium', position, sx }) => {
  // ユーザータイプによって色を変更（看護師=ピンク系、患者=緑系）
  const isNurse = userType === 'nurse';
  
  // タイプに応じたテーマの取得
  const theme = isNurse ? NURSE_TYPE_THEME.NORMAL : PATIENT_THEME.MYSELF;
  const roleLabel = position === '親' 
    ? `親: ${isNurse ? '看護師' : '患者'}` 
    : (isNurse ? '看護師' : '患者');
  
  // リンク先のURLを取得
  const getLinkUrl = () => {
    if (!userInfo) return '#';
    return isNurse ? `/nurses/${userInfo.id}` : `/patients/${userInfo.id}`;
  };

  // サイズに応じたスタイル調整
  const chipHeight = size === 'small' ? '16px' : '18px';
  const chipFontSize = size === 'small' ? '0.65rem' : '0.7rem';
  const nameFontSize = size === 'small' ? '0.8rem' : '0.9rem';
  const nicknameFontSize = size === 'small' ? '0.75rem' : '0.85rem';

  // 親の看護師の場合は四角形に
  const borderRadius = position === '親' && isNurse ? '4px' : '16px';

  // ユーザー情報がない場合はハイフン表示
  if (!userInfo) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', ...sx }}>
        <Chip
          label={roleLabel}
          size="small"
          sx={{
            height: chipHeight,
            fontSize: chipFontSize,
            backgroundColor: '#f5f5f5',
            color: 'text.secondary',
            borderRadius: '16px',
            mr: 1
          }}
        />
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', ...sx }}>
      {/* ユーザータイプ表示 */}
      <Chip
        label={roleLabel}
        size="small"
        sx={{
          height: chipHeight,
          fontSize: chipFontSize,
          backgroundColor: theme.BACKGROUND,
          color: theme.DARK,
          border: `1px solid ${theme.BORDER}`,
          borderRadius: borderRadius,
          fontWeight: 'normal',
          mr: 1
        }}
      />
      
      {/* ユーザー名/ニックネーム表示 */}
      <Link
        to={getLinkUrl()}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'bold',
            fontSize: nameFontSize,
            '&:hover': { color: theme.DARK }
          }}
        >
          {userInfo.name}
          {userInfo.nickname && (
            <Typography 
              component="span" 
              variant="body2" 
              sx={{ 
                fontSize: nicknameFontSize, 
                ml: 0.5,
                fontWeight: 'normal' 
              }}
            >
              / {userInfo.nickname}
            </Typography>
          )}
        </Typography>
      </Link>
    </Box>
  );
};

export default UserRoleChip;